import axios from 'axios';
import { put } from 'redux-saga/effects';
import {
    fetchSitesFailure,
    fetchSitesSuccess,
    fetchInheritedSitesFailure,
    fetchInheritedSitesSuccess,
} from 'src/features/resources-assignment/slice';
import { getUrl } from 'src/services/ApiService';
import apiClient from 'src/services/apiClient';
import { RESOURCES_TYPES } from '../constants';
import { getFilterQueryString, getSortQueryString } from '../utils';
import { getTransformedSortModel } from '../components/Sites/util';
import { formatMessage } from 'src/i18n/intl';
import Locale from 'src/i18n';

export const SITES_API_CACHE_ID = 'cache:sites';

const getCachePredicateIgnoreUrls = () => {
    // Cache only if sortBy is company.name asc
    // Don't cache request if url contains any of these patterns
    return [
        /cursor/,
        /filter/,
        /sortBy=name/,
        /sortBy=friendlyName/,
        /sortBy=company\.name\sdesc/,
        /sortBy=primaryAddress/,
        /sortBy=type/,
    ];
};

const getApiMetaData = (activeConnectedAppId, queryOptions, filterModel, sortModel) => {
    let endpoint = `/iambff/v1/connected-apps/${activeConnectedAppId}/corporate-structure/sites`;
    endpoint += `?limit=${queryOptions.limit}`;
    endpoint += queryOptions.cursor ? `&cursor=${queryOptions.cursor}` : '';

    let filterQueryString = getFilterQueryString(filterModel, RESOURCES_TYPES.SITE);
    if (filterQueryString) {
        endpoint += `&filter=${filterQueryString}`;
    }

    if (sortModel) {
        const transformedSortModel = getTransformedSortModel(sortModel);
        const sortQueryString = getSortQueryString(transformedSortModel);
        if (sortQueryString) {
            endpoint += `&sortBy=${sortQueryString}`;
        }
    }

    return { endpoint, filterQueryString };
};

export function* getSites(action) {
    const { activeConnectedAppId, queryOptions, filterModel, sortModel, totalRowCount, signal } = action.payload;
    const { endpoint, filterQueryString } = getApiMetaData(activeConnectedAppId, queryOptions, filterModel, sortModel);

    try {
        const result = yield apiClient.get(getUrl(endpoint), {
            cache: {
                ttl: 1000 * 60 * 3, // 3 minutes
                id: SITES_API_CACHE_ID,
                cachePredicate: {
                    ignoreUrls: getCachePredicateIgnoreUrls(),
                },
            },
            signal,
        });
        const siteTotalCount = result.headers.get('x-user-sites-count');
        if (siteTotalCount === '') {
            const result = {
                isSuccess: false,
                error: formatMessage(Locale.siteErrorStateCount),
            };
            yield put(fetchSitesFailure(result));
            return;
        }
        const nextCursor = result.headers.get('x-next-cursor');
        const pageInfo = {
            nextCursor,
            hasNextPage: !!nextCursor,
        };

        if (filterQueryString) {
            pageInfo.totalRowCount = totalRowCount;
            pageInfo.totalRowCountWithFilter = Number.parseInt(siteTotalCount);
        } else {
            pageInfo.totalRowCount = Number.parseInt(siteTotalCount);
            pageInfo.totalRowCountWithFilter = undefined;
        }
        yield put(
            fetchSitesSuccess({
                rows: result.data,
                pageInfo,
            })
        );
    } catch (error) {
        const { message } = error;
        if (axios.isCancel(error)) {
            return;
        }
        const result = {
            isSuccess: false,
            error: message,
        };
        yield put(fetchSitesFailure(result));
    }
}

export function* getInheritedSites(action) {
    const { activeConnectedAppId, queryOptions, filterModel, signal } = action.payload;
    const { endpoint } = getApiMetaData(activeConnectedAppId, queryOptions, filterModel, null);

    try {
        const result = yield apiClient.get(getUrl(endpoint), {
            cache: false,
            signal,
        });
        yield put(
            fetchInheritedSitesSuccess({
                rows: result.data,
            })
        );
    } catch (error) {
        const { message } = error;
        if (axios.isCancel(error)) {
            return;
        }
        yield put(
            fetchInheritedSitesFailure({
                error: message,
                isSuccess: false,
            })
        );
    }
}
